import { useSyncExternalStore } from 'react';

import { BREAKPOINTS, FORMATS, type Format } from '@/tokens/breakpoints';

/**
 * The current screen format — reserved for the cases where the DOM structure
 * genuinely changes between phone and desktop (bottom bar versus rail, sheet
 * versus dialog).
 *
 * Warning: Never use it for style. Padding, type scale and column counts are
 * responsive Tailwind classes (`p-2 desktop:p-6`): no JS, no re-render, and a
 * correct result during the resize itself.
 *
 * Built on `matchMedia` plus `useSyncExternalStore` rather than a `resize`
 * listener: the browser then only notifies on threshold crossings, and there is
 * no tearing under React 19.
 */

const QUERY: Record<Format, string> = {
  mobile: `(max-width: ${BREAKPOINTS.tablet - 0.02}px)`,
  tablet: `(min-width: ${BREAKPOINTS.tablet}px) and (max-width: ${BREAKPOINTS.desktop - 0.02}px)`,
  desktop: `(min-width: ${BREAKPOINTS.desktop}px) and (max-width: ${BREAKPOINTS.wide - 0.02}px)`,
  wide: `(min-width: ${BREAKPOINTS.wide}px)`,
};

// Deliberately not memoised at module level: `matchMedia` is cheap, and a
// global cache would make the hook blind to a test environment replacing
// `window.matchMedia` after the first import.
const mediaLists = () =>
  FORMATS.map((format) => globalThis.matchMedia(QUERY[format]));

function subscribe(onStoreChange: () => void) {
  const lists = mediaLists();
  lists.forEach((media) => media.addEventListener('change', onStoreChange));
  return () =>
    lists.forEach((media) =>
      media.removeEventListener('change', onStoreChange),
    );
}

// Returns a string: `useSyncExternalStore` compares by identity, so a
// primitive is stable without memoisation.
function getSnapshot(): Format {
  const lists = mediaLists();
  return FORMATS.find((_, index) => lists[index].matches) ?? 'mobile';
}

export function useFormat(): Format {
  return useSyncExternalStore(subscribe, getSnapshot, () => 'mobile');
}
